import React, { useEffect, useMemo, useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import { fetchDeliverableDates, type DateMap } from '../../utils/deliverableDates';
import { addClientContact, draftBody, draftSubject, fetchClientContacts, fetchTold, looksLikeEmail, removeClientContact, tellThem, type ClientContact } from '../../utils/clientTell';
import { TellWho } from './TellWho';
import { BUTTON_SOLID, FIELD } from './adminStyles';
import type { Deliverable } from '../../types/portal';

/**
 * PUTTING IT UP AND TELLING THEM.
 *
 * Tom: *"it was getting messy to put the next one up. Because they couldn't
 * see it."* Two jobs, so the second one did not happen.
 *
 * This sits directly under Deliverables, so the moment a cut is marked ready
 * and shown, the drafted email is the next thing on the screen. It would be
 * better still inside the deliverable row itself — that was the design — but
 * `components/portal/DeliverableAdmin.tsx` is ~28KB and the artifact editor
 * silently truncates a whole-file write well below that. If that file is ever
 * split, move this into the row and delete this paragraph.
 *
 * **It only ever knows about pieces put up since 14 Oct 2026**, when
 * `client_added_at` started being stamped. Everything older is invisible to
 * it, so it starts empty on every existing project rather than opening with a
 * backlog of things nobody is going to announce now.
 *
 * **Nothing here nags.** A piece that is up and not announced is listed,
 * plainly, with the message already written. There is no badge, no count on a
 * tab and no second state — you tell them or you don't.
 *
 * ---
 *
 * **THE EMPTY STATE IS ONE COLUMN, NOT TWO. 19 Oct 2026.**
 *
 * It used to hold its full two-column grid whether or not it had anything to
 * say: an empty seven-column well on the left carrying a sentence explaining
 * how the feature worked, and the contact list on the right. On a project in
 * pre-production — Marjan, 25 tracker steps and no deliverables at all —
 * that is roughly half a screen of furniture sitting between Deliverables
 * and the Project Tracker, saying nothing. Tom: *"this email notification is
 * messy and getting lost"*.
 *
 * He asked whether it should open on a page of its own. It should not. The
 * whole value of this panel is that it is the next thing you see after
 * marking a cut ready — put it behind a click and you add a trip and a new
 * place to forget to look, which is the problem, not the cure.
 *
 * So the panel keeps its position and loses its bulk. With nothing waiting it
 * is the heading line plus the contact list, and the explanatory sentence is
 * gone: a section that explains itself is a section that needed explaining,
 * and the heading's own aside already reads "nobody to tell yet". The
 * contact list stays visible in both states deliberately — you want to be
 * able to set up who hears about cuts before the first one exists, rather
 * than being sent to find three addresses at the moment you meant to press
 * send.
 */
export function TellThem() {
  const {
    activeProject,
    notify
  } = usePortal();
  const projectId = activeProject.id;
  const [contacts, setContacts] = useState<ClientContact[]>([]);
  const [told, setTold] = useState<Map<string, string>>(() => new Map());
  const [dates, setDates] = useState<DateMap>(() => new Map());
  const [loaded, setLoaded] = useState(false);
  const [pickedId, setPickedId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [touched, setTouched] = useState(false);
  const [off, setOff] = useState<Set<string>>(() => new Set());
  const [sending, setSending] = useState(false);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  async function reload() {
    const [people, already, when] = await Promise.all([fetchClientContacts(projectId), fetchTold(projectId), fetchDeliverableDates(projectId)]);
    setContacts(people);
    setTold(already);
    setDates(when);
    setLoaded(true);
  }
  useEffect(() => {
    if (!projectId) return;
    let live = true;
    void Promise.all([fetchClientContacts(projectId), fetchTold(projectId), fetchDeliverableDates(projectId)]).then(([people, already, when]) => {
      if (!live) return;
      setContacts(people);
      setTold(already);
      setDates(when);
      setLoaded(true);
    });
    return () => {
      live = false;
    };
  }, [projectId]);

  /* Up, openable, and never announced. Anything without a stamped date went
     up before this existed and is left alone. */
  const waiting = useMemo(() => activeProject.deliverables.filter(item => item.visibleToClient && item.type !== 'Transcript' && item.status === 'Ready for Review' && Boolean(dates.get(item.id)?.added) && !told.has(item.id)), [activeProject.deliverables, dates, told]);
  const picked: Deliverable | undefined = waiting.find(item => item.id === pickedId) ?? waiting[0];

  /* The draft follows whichever piece is in hand, until someone types. */
  useEffect(() => {
    if (!picked || touched) return;
    setSubject(draftSubject(picked));
    setBody(draftBody(picked, told.size === 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked?.id, touched]);
  const recipients = contacts.filter(person => !off.has(person.id)).map(person => person.email);
  async function send() {
    if (!picked || sending || recipients.length === 0) return;
    setSending(true);
    const result = await tellThem(projectId, picked.id, subject, body, recipients);
    setSending(false);
    if (!result.ok) {
      notify(`Not sent · ${result.error}`);
      return;
    }
    notify(`${picked.name} · told ${recipients.length} ${recipients.length === 1 ? 'person' : 'people'}`);
    setTouched(false);
    setPickedId('');
    void reload();
  }
  async function add(event: React.FormEvent) {
    event.preventDefault();
    if (!looksLikeEmail(email)) {
      notify(`“${email.trim()}” doesn’t look like an email address`);
      return;
    }
    const result = await addClientContact(projectId, name, email);
    if (!result.ok) {
      notify(`Not added · ${result.error}`);
      return;
    }
    setName('');
    setEmail('');
    setAdding(false);
    void reload();
  }
  if (!loaded) return null;
  const who = <TellWho contacts={contacts} off={off} onToggle={id => setOff(current => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);else next.add(id);
    return next;
  })} onRemove={async id => {
    await removeClientContact(id);
    void reload();
  }} adding={adding} setAdding={setAdding} name={name} setName={setName} email={email} setEmail={setEmail} onAdd={add} data-id="element-1606" data-mp="30vyfc7pu75br" />;
  return <section aria-labelledby="tell-them" className="mt-16" data-id="element-1607" data-mp="3r7058vw86wrn">
      <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-ruleDark pb-4" data-id="element-1608" data-mp="3w4ihow9gbu4e">
        <h2 id="tell-them" className="label m-0 text-paper/40" data-id="element-1609" data-mp="3idwyt7jzzk6i">
          Telling The Client
        </h2>
        <p className="label-sm m-0 text-paper/35" data-id="element-1610" data-mp="3me72gdkbhyi7">
          {contacts.length === 0 ? 'Nobody to tell yet' : `${contacts.length} ${contacts.length === 1 ? 'person' : 'people'}`}
        </p>
      </div>

      {picked ? <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-1611" data-mp="3dflkajirclhy">
          <div className="lg:col-span-7" data-id="element-1612" data-mp="2zg3txzptd06h">
            <p className="label text-paper/40" data-id="element-1613" data-mp="33g4kgiq6t1tw">Up, and not yet announced</p>
            <p className="mt-4 font-display text-3xl leading-none sm:text-4xl" data-id="element-1614" data-mp="33i77quzyggux">{picked.name}</p>
            <p className="label-sm mt-4 text-paper/35" data-id="element-1615" data-mp="xnxur962pv96">
              On their page since {dates.get(picked.id)?.added ?? ''} · leads the page
            </p>

            {waiting.length > 1 && <div className="mt-7 flex flex-wrap gap-x-7 gap-y-3" data-id="element-1616" data-mp="3lk0pp6ebfgzx">
                {waiting.map(item => <button key={item.id} type="button" onClick={() => {
            setPickedId(item.id);
            setTouched(false);
          }} aria-pressed={item.id === picked.id} className={['label-sm transition-colors duration-150 ease-out', item.id === picked.id ? 'text-paper' : 'text-paper/35 hover:text-paper/70'].join(' ')} data-id="element-1617" data-mp="3go1sctb0eond">
                    {item.name}
                  </button>)}
              </div>}

            <label className="mt-9 block" data-id="element-1618" data-mp="r3gnflomqpk7">
              <span className="label block text-paper/40" data-id="element-1619" data-mp="m5ngurjeztje">Subject</span>
              <input value={subject} onChange={event => {
            setSubject(event.target.value);
            setTouched(true);
          }} className={FIELD} data-id="element-1620" data-mp="m6gdv0skn4oc" />
            </label>

            <label className="mt-8 block" data-id="element-1621" data-mp="13eps2u29gszi">
              <span className="label block text-paper/40" data-id="element-1622" data-mp="13h2fhmqsfvjy">What to say</span>
              <textarea value={body} rows={3} onChange={event => {
            setBody(event.target.value);
            setTouched(true);
          }} className={`${FIELD} resize-none leading-relaxed`} data-id="element-1623" data-mp="18dkhwfynbht4" />
            </label>
            <p className="label-sm mt-4 text-paper/30" data-id="element-1624" data-mp="kqkrfvgwho4x">
              Written for you · change any of it. The email itself already tells them where to
              approve it and how to ask for a change.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-1625" data-mp="212re38amnvb7">
              <button type="button" onClick={() => void send()} disabled={sending || recipients.length === 0} className={`${BUTTON_SOLID} disabled:cursor-default disabled:opacity-40`} data-id="element-1626" data-mp="210wvi0mtwfaa">
                {sending ? 'Sending' : `Send to ${recipients.length}`}
              </button>
              <p className="label-sm m-0 text-paper/30" data-id="element-1627" data-mp="29l7byvem18dh">
                One email each, from studio@portalproductions.me. It is already on their page —
                this only tells them.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5 lg:border-l lg:border-ruleDark lg:pl-12" data-id="element-1628" data-mp="1hu1ekmq9xorw">{who}</div>
        </div> : (/* Nothing waiting: one column, no empty well, no explanation. */
    <div className="mt-8 max-w-md" data-id="element-1629" data-mp="1qvr8bz7mozsu">{who}</div>)}
    </section>;
}